import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { crmWebhookLogs, type CrmWebhookLog } from "../../api/crm-webhook-logs.service.js";
import styles from "./WebhookLogTimeline.module.css";

export type WebhookLogTimelineProps = {
  correlationId: string | null;
  currentLogId: string;
};

export function WebhookLogTimeline({ correlationId, currentLogId }: WebhookLogTimelineProps) {
  const navigate = useNavigate();
  const [logs, setLogs] = useState<CrmWebhookLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    if (!correlationId) {
      setLoading(false);
      return;
    }
    loadTimeline(correlationId);
  }, [correlationId]);

  async function loadTimeline(corrId: string) {
    try {
      setLoading(true);
      const response = await crmWebhookLogs.getAll({ correlationId: corrId, limit: 100 });
      const sorted = response.logs.sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );
      setLogs(sorted);
    } catch {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }

  function getStatusClass(status: string) {
    switch (status) {
      case "created":
        return styles.statusCreated;
      case "duplicate":
        return styles.statusDuplicate;
      case "failed":
        return styles.statusFailed;
      case "dropped":
        return styles.statusDropped;
      default:
        return "";
    }
  }

  function formatTimelineTime(dateString: string) {
    const date = new Date(dateString);
    const day = date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
    const time = date.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    return `${day}, ${time}`;
  }

  if (loading || logs.length < 2) {
    return null;
  }

  return (
    <div className={styles.section}>
      <button className={styles.toggle} onClick={() => setExpanded(!expanded)}>
        <span>{expanded ? "▼" : "▶"}</span>
        <span>
          Retry history ({logs.length} {logs.length === 1 ? "entry" : "entries"})
        </span>
      </button>
      {expanded && (
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.th}>Type</th>
              <th className={styles.th}>Status</th>
              <th className={styles.th}>Error</th>
              <th className={styles.th}>Time</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((entry, index) => {
              const isCurrent = entry.id === currentLogId;
              const isOriginal = index === logs.length - 1;

              return (
                <tr
                  key={entry.id}
                  className={`${styles.row} ${isCurrent ? styles.currentRow : styles.clickableRow}`}
                  onClick={() => {
                    if (!isCurrent) navigate(`/fulfilment/crm-webhook-logs/${entry.id}`);
                  }}
                >
                  <td className={styles.td}>{isOriginal ? "Original" : "Retry"}</td>
                  <td className={styles.td}>
                    <span className={`${styles.statusBadge} ${getStatusClass(entry.status)}`}>
                      {entry.status}
                    </span>
                  </td>
                  <td className={`${styles.td} ${styles.errorCell}`}>{entry.errorCode || "—"}</td>
                  <td className={`${styles.td} ${styles.timeCell}`}>
                    {formatTimelineTime(entry.createdAt)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
